import React, { useLayoutEffect, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Button, Card, Col, Form, Modal, Row, Table, Pagination } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getAllData, addNewTransaction, updateTransaction, deleteTransaction } from "../actions/dailyActions";
import { adminGetAllUsers } from "../actions/userActions";
import FormField from "../components/FormField";
import NoKeyboardInput from "../components/NoKeyboardInput";
import useIsMobile from "../utils/useIsMobile";

const format_rupees = val =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);

const initialTransactionState = {
  date: new Date(),
  particulars: "",
  amount: "",
  type: "Debit",
  data: null,
};

const DailyBook = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userLogin.userInfo);
  const transactions = useSelector(state => state.transactions.data);
  const openingBalance = useSelector(state => state.transactions.opening_balance);
  const pendingBalance = useSelector(state => state.transactions.pending_balance);
  const loading = useSelector(state => state.transactions.loading);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [show, setShow] = useState(false);
  const [transaction, setTransaction] = useState(initialTransactionState);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const isAdmin = userInfo && userInfo.role === "admin";
  const { users } = useSelector(state => state.adminUsers || { users: [] });
  const [selectedUser, setSelectedUser] = useState("");
  const [modalUser, setModalUser] = useState("");
  const isMobile = useIsMobile();

  // Calculate balances
  const balance = useMemo(() => {
    let totalCredit = 0, totalDebit = 0;
    if (transactions) {
      transactions.forEach(({ type, amount }) => {
        if (type === "Credit") totalCredit += Math.abs(amount);
        if (type === "Debit") totalDebit += Math.abs(amount);
      });
    }
    return {
      totalCredit,
      totalDebit,
      balance: (openingBalance + totalCredit) - totalDebit,
    };
  }, [transactions, openingBalance]);

  // Fetch data on mount or search
  const handleSearch = useCallback(() => {
    const filters = { start_date: startDate, end_date: endDate };
    if (isAdmin && selectedUser) filters.userId = selectedUser;
    dispatch(getAllData(filters));
    setCurrentPage(1); // Reset to first page on search
  }, [dispatch, startDate, endDate, isAdmin, selectedUser]);

  // Fetch users for admin
  useEffect(() => {
    if (isAdmin) dispatch(adminGetAllUsers());
  }, [isAdmin, dispatch]);

  const currentItems = useMemo(() => {
    if (!transactions) return [];
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return transactions.slice(indexOfFirstItem, indexOfLastItem);
  }, [transactions, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return transactions ? Math.ceil(transactions.length / itemsPerPage) : 0;
  }, [transactions, itemsPerPage]);

  useLayoutEffect(() => { handleSearch(); }, [handleSearch]);

  // Handlers
  const clearFields = useCallback(() => setTransaction(initialTransactionState), []);
  const handleClose = () => { clearFields(); setModalUser(""); setShow(false); };
  const handleShow = () => { setModalUser(selectedUser); setShow(true); };

  const handleSave = () => {
    const { date, particulars, amount, type } = transaction;
    const targetUserId = modalUser || selectedUser;
    const targetUser = users && users.find(u => String(u.user_id) === String(targetUserId));
    dispatch(addNewTransaction({
      transacion_date: date,
      description: particulars,
      amount,
      type,
      ...(isAdmin ? {
        userId: targetUserId,
        company: targetUser ? targetUser.company : null
      } : {}),
    }));
    clearFields();
    setShow(false);
    handleSearch();
  };

  const editTransentry = (val, index) => {
    setTransaction({
      date: new Date(val.transacion_date),
      particulars: val.description,
      amount: val.amount,
      type: val.type,
      data: { id: val.id, index },
    });
    setShow(true);
  };

  const updateTransentry = () => {
    const { date, particulars, amount, type, data } = transaction;
    dispatch(updateTransaction({
      id: data.id,
      transacion_date: date,
      description: particulars,
      amount,
      type,
    }, data.index));
    clearFields();
    setShow(false);
    handleSearch();
  };

  const deleteTransentry = (e, id, index) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this entry?")) {
      dispatch(deleteTransaction(id, index));
      handleSearch();
    }
  };

  const datePickerRef = useRef(null);

  const onDateChange = dates => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    // Close calendar once end date is selected (handles same-date range too)
    if (end) {
      datePickerRef.current?.setOpen(false);
    }
  };

  const enableEdit = value =>
    new Date().toDateString() !== new Date(value.transacion_date).toDateString() || value.invoice_no;

  return (
    <div className="day-book-container pb-5">
      {/* Page Header & Actions Row */}
      <Row className="mb-4 align-items-center">
        <Col md={4} sm={8} xs={8}>
          <h3 style={{ fontSize: 'clamp(1rem, 4vw, 1.4rem)', fontWeight: 700, color: 'var(--text-dark)', letterSpacing: '-0.5px', margin: 0 }}>Day Book</h3>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Track your daily transactions</p>

        </Col>
        {isMobile &&
          <Col xs={4} sm={4} className="text-right">
            <Button onClick={handleShow} variant="success" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}>
              + Add
            </Button>
          </Col>
        }
        <Col md={8} className={isMobile ? "pt-3" : ""}>
          <div className={`d-flex ${isMobile ? "justify-content-between" : "justify-content-end"} align-items-center flex-wrap`} style={{ gap: '12px' }}>
            <div className="datepicker-input-wrapper">
              <i className="fa fa-calendar"></i>
              <DatePicker
                ref={datePickerRef}
                placeholderText="Select Date Range"
                selected={startDate}
                onChange={onDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="dd MMM yyyy"
                maxDate={new Date()}
                withPortal={isMobile}
                customInput={
                  <NoKeyboardInput
                    style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.85rem', fontWeight: 500, color: '#334155', cursor: 'pointer', minWidth: '160px' }}
                  />
                }
              />
            </div>
            <Button onClick={handleSearch} variant="primary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}>
              <i className="fa fa-search mr-2"></i>Search
            </Button>
            {!isMobile &&
              <Button onClick={handleShow} variant="success" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }}>
                + Add
              </Button>}
          </div>
        </Col>
      </Row>

      {/* Admin: Company Filter */}
      {isAdmin && (
        <Row className="mb-4">
          <Col xs={12} md={4}>
            <FormField label="Filter by User">
              <Form.Control
                as="select"
                className="bg-white"
                style={{ border: '1px solid #cbd5e1', borderRadius: '8px', height: '50px', fontWeight: 500 }}
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                <option value="">All Users</option>
                {users && users.filter(u => u.role !== 'admin').map((user) => (
                  <option key={user.user_id} value={user.user_id}>{user.user_name}</option>
                ))}
              </Form.Control>
            </FormField>
          </Col>
        </Row>
      )}

      {/* Metrics Row */}
      <Row className="mb-4">
        <Col xs={12} sm={6} md={4} lg className="mb-3">
          <div className="metric-card-compact border-accent-indigo custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Opening Balance</p>
            <h4 className="mb-0 font-weight-bold text-dark">{format_rupees(openingBalance)}</h4>
          </div>
        </Col>
        <Col xs={12} sm={6} md={4} lg className="mb-3">
          <div className="metric-card-compact border-accent-emerald custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Received</p>
            <h4 className="mb-0 font-weight-bold text-success">{format_rupees(balance.totalCredit)}</h4>
          </div>
        </Col>
        <Col xs={12} sm={6} md={4} lg className="mb-3">
          <div className="metric-card-compact border-accent-rose custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Payment</p>
            <h4 className="mb-0 font-weight-bold text-danger">{format_rupees(balance.totalDebit)}</h4>
          </div>
        </Col>
        <Col xs={12} sm={6} md={6} lg className="mb-3">
          <div className="metric-card-compact border-accent-amber custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px' }}>Pending</p>
            <h4 className="mb-0 font-weight-bold" style={{ color: '#f59e0b' }}>{format_rupees(pendingBalance)}</h4>
          </div>
        </Col>
        <Col xs={12} sm={12} md={6} lg className="mb-3">
          <div className="metric-card-compact border-accent-purple custom-card-boxshadow p-3">
            <p className="text-muted small text-uppercase font-weight-bold mb-1" style={{ letterSpacing: '0.5px', opacity: 0.8 }}>Closing Balance</p>
            <h4 className="mb-0 font-weight-bold text-dark">{format_rupees(balance.balance)}</h4>
          </div>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card className="custom-card-boxshadow border-0 rounded-lg">
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0" bordered={false} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ backgroundColor: '#f8fafc' }}>
              <tr>
                {isAdmin && <th className="py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold">User / Lab</th>}
                <th className="text-center py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold" width="150">Date</th>
                <th className="py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold">Particulars</th>
                <th className="text-right py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold">Debit</th>
                <th className="text-right py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold">Credit</th>
                <th width="150" className="text-center py-3 border-top-0 border-bottom text-muted text-uppercase small font-weight-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? "6" : "5"} className="text-center py-4 text-muted">Loading transactions...</td></tr>
              ) : currentItems && currentItems.length > 0 ? currentItems.map((val, index) => (
                <tr key={index}>
                  {isAdmin && (
                    <td className="align-middle py-2 border-bottom font-weight-medium">
                      <div className="font-weight-bold">{val.user_name || 'System / Admin'}</div>
                      <div className="small text-muted">{val.com_name || 'N/A'}</div>
                    </td>
                  )}
                  <td className="text-center align-middle py-2 border-bottom">{new Date(val.transacion_date).toLocaleDateString("en-IN")}</td>
                  <td className="align-middle py-2 border-bottom font-weight-medium">{val.description}</td>
                  <td className="text-right align-middle py-2 border-bottom">{val.type === "Debit" ? <span className="text-danger font-weight-bold">{format_rupees(val.amount)}</span> : <span className="text-muted">-</span>}</td>
                  <td className="text-right align-middle py-2 border-bottom">{val.type === "Credit" ? <span className="text-success font-weight-bold">{format_rupees(val.amount)}</span> : <span className="text-muted">-</span>}</td>
                  <td className="text-center align-middle py-2 border-bottom">
                    {!enableEdit(val) ? (
                      <>
                        <Button disabled={enableEdit(val)} title="Edit Entry" variant="link" size="sm" className="p-1 mx-1 text-primary" onClick={() => editTransentry(val, index)}>
                          <i className="fa fa-pencil fa-lg"></i>
                        </Button>
                        <Button disabled={enableEdit(val)} title="Delete Entry" variant="link" size="sm" className="p-1 mx-1 text-danger" onClick={e => deleteTransentry(e, val.id, index)}>
                          <i className="fa fa-trash fa-lg"></i>
                        </Button>
                      </>
                    ) : <span className="text-muted small">Auto-generated</span>}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={isAdmin ? "6" : "5"} className="text-center py-5 text-muted">No transactions found in this period.</td></tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-4 py-3 bg-white border-top rounded-bottom">
              <div className="text-muted small font-weight-medium mb-2 mb-md-0">
                Showing <span className="text-dark font-weight-bold">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="text-dark font-weight-bold">{Math.min(currentPage * itemsPerPage, transactions.length)}</span> of <span className="text-dark font-weight-bold">{transactions.length}</span> results
              </div>

              <Pagination className="mb-0 custom-pagination">
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &laquo; Previous
                </Pagination.Prev>

                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  // Only show current page, plus/minus 1, and first/last for cleaner display if many pages
                  if (totalPages > 7) {
                    if (pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                      if (Math.abs(pageNum - currentPage) === 2) return <Pagination.Ellipsis key={pageNum} disabled />;
                      return null;
                    }
                  }

                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}

                <Pagination.Next
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next &raquo;
                </Pagination.Next>
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal className="mt-5 border-0" show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title style={{ fontWeight: 700, color: 'var(--text-dark)' }}>
            {transaction.data ? 'Edit' : 'Add New'} Transaction
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form>
            <Row>
              <Col xs={12} className="mb-3">
                <Form.Group controlId="transactionType" className="mb-0">
                  <Form.Label className="small text-muted font-weight-bold text-uppercase">Transaction Type</Form.Label>
                  <div className="d-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-gradient-start)' }}>
                    <div
                      onClick={() => setTransaction(t => ({ ...t, type: "Debit" }))}
                      className="cursor-pointer text-center py-2 flex-grow-1"
                      style={{
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        backgroundColor: transaction.type === "Debit" ? '#ef4444' : 'transparent',
                        color: transaction.type === "Debit" ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      Debit / Payment
                    </div>
                    <div
                      onClick={() => setTransaction(t => ({ ...t, type: "Credit" }))}
                      className="cursor-pointer text-center py-2 flex-grow-1"
                      style={{
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        backgroundColor: transaction.type === "Credit" ? '#10b981' : 'transparent',
                        color: transaction.type === "Credit" ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      Credit / Received
                    </div>
                  </div>
                </Form.Group>
              </Col>
              {isAdmin && (
                <Col xs={12} className="mb-3">
                  <Form.Group controlId="transactionUser">
                    <Form.Label className="small text-muted font-weight-bold text-uppercase">Select User</Form.Label>
                    <Form.Control
                      as="select"
                      className="bg-white"
                      value={modalUser}
                      onChange={e => setModalUser(e.target.value)}
                    >
                      <option value="">-- Select a User --</option>
                      {users && users.filter(u => u.role !== 'admin').map((user) => (
                        <option key={user.user_id} value={user.user_id}>{user.user_name}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
              <Col xs={12} md={6} className="mb-3">
                <Form.Group controlId="transactionDate">
                  <Form.Label className="small text-muted font-weight-bold text-uppercase">Date</Form.Label>
                  <div className="d-block w-100">
                    <DatePicker
                      placeholderText="Select Date"
                      className="form-control bg-white"
                      selected={transaction.date}
                      onChange={date => setTransaction(t => ({ ...t, date }))}
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                      wrapperClassName="w-100"
                      withPortal={isMobile}
                      customInput={
                        <NoKeyboardInput className="form-control bg-light border-0" style={{ cursor: 'pointer' }} />
                      }
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Group controlId="transactionAmount">
                  <Form.Label className="small text-muted font-weight-bold text-uppercase">Amount</Form.Label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-white border-right-0">₹</span>
                    </div>
                    <Form.Control
                      value={transaction.amount}
                      type="number"
                      className="border-left-0 pl-2"
                      style={{ boxShadow: 'none' }}
                      placeholder="0.00"
                      min={0}
                      onChange={e => setTransaction(t => ({ ...t, amount: e.target.value }))}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} className="mb-2">
                <Form.Group controlId="transactionParticulars">
                  <Form.Label className="small text-muted font-weight-bold text-uppercase">Particulars / Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter short description"
                    value={transaction.particulars}
                    onChange={e => setTransaction(t => ({ ...t, particulars: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top pt-3 pb-3 px-4 bg-light">
          <Button variant="outline-secondary" onClick={handleClose} style={{ fontWeight: 600, padding: '8px 24px', borderRadius: '6px' }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ fontWeight: 600, padding: '8px 32px', borderRadius: '6px', opacity: (transaction.particulars && transaction.amount && transaction.type && transaction.date) ? 1 : 0.65 }}
            onClick={transaction.data ? updateTransentry : handleSave}
            disabled={!(transaction.particulars && transaction.amount && transaction.type && transaction.date && (!isAdmin || modalUser))}
          >
            {transaction.data ? 'Update' : 'Save Transaction'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DailyBook;
